/**
 * THIS FILE IS AUTO-GENERATED. DO NOT EDIT MANUALLY.
 * Generated from ../../api/optimus_openapi.json
 * Generated by generate-optimus-view-queries.ts
 * To regenerate, run the following command in the frontend folder:
 *    pnpm run generate:integration
 * Contact the Core Team if any issues arise.
 */

import { keepPreviousData, QueryKey, queryOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'

import { handleApiRequest } from '../hooks/tanstack-query/optimus/shared/apiWrapper'
import { operations } from './optimus-api-types.generated'
import { optimusBaseClient } from './optimus-client'

export type ViewQueryOptions<TQueryFnData, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey> = Omit<
    UseQueryOptions<TQueryFnData, Error, TData, TQueryKey>,
    'queryKey' | 'queryFn' | 'queryKeyHashFn'
>

async function requireViewData<T>(request: Promise<T | undefined>): Promise<T> {
    const data = await request
    if (data === undefined) throw new Error('View query returned no response data')
    return data
}

/**
 * List Daily Constraints
 * 
 * List all daily constraints with resolved surgery type names.

Returns all configured daily constraints with their codes, names, capacities,
and the resolved names of referenced hospital surgery types and surgery type groups.
 */
type DailyConstraintsQueryData = operations['list_daily_constraints_hospital_constraints_daily__get']['responses'][200]['content']['application/json']

const getDailyConstraintsQueryKey = () => ['view', 'DailyConstraints'] as const
type DailyConstraintsQueryKey = ReturnType<typeof getDailyConstraintsQueryKey>

export const getDailyConstraintsQueryOptions = <TData = DailyConstraintsQueryData>(
    options?: ViewQueryOptions<DailyConstraintsQueryData, TData, DailyConstraintsQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getDailyConstraintsQueryKey(),
        queryFn: ({ signal }) => requireViewData(handleApiRequest(() => optimusBaseClient.GET('/hospital-constraints/daily/', { signal }))),
    })

export const useDailyConstraints = <TData = DailyConstraintsQueryData>(
    options?: ViewQueryOptions<DailyConstraintsQueryData, TData, DailyConstraintsQueryKey>
) => useQuery(getDailyConstraintsQueryOptions(options))

/**
 * Check All Constraints
 * 
 * Combined evaluation of scheduled surgeries against both equipment daily constraints
and post-op capacity constraints, as used in loading date ranges in optimus/planning.
 */
type CheckAllConstraintsQueryData =
    operations['check_all_constraints_hospital_constraints_daily_check_all_constraints__post']['responses'][200]['content']['application/json']

const getCheckAllConstraintsQueryKey = (
    queryParams: operations['check_all_constraints_hospital_constraints_daily_check_all_constraints__post']['parameters']['query'],
    body: operations['check_all_constraints_hospital_constraints_daily_check_all_constraints__post']['requestBody']['content']['application/json']
) => ['view', 'checkAllConstraints', queryParams, body] as const
type CheckAllConstraintsQueryKey = ReturnType<typeof getCheckAllConstraintsQueryKey>

export const getCheckAllConstraintsQueryOptions = <TData = CheckAllConstraintsQueryData>(
    queryParams: operations['check_all_constraints_hospital_constraints_daily_check_all_constraints__post']['parameters']['query'],
    body: operations['check_all_constraints_hospital_constraints_daily_check_all_constraints__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckAllConstraintsQueryData, TData, CheckAllConstraintsQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getCheckAllConstraintsQueryKey(queryParams, body),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() =>
                    optimusBaseClient.POST('/hospital-constraints/daily/check_all_constraints/', { params: { query: queryParams }, body, signal })
                )
            ),
    })

export const useCheckAllConstraints = <TData = CheckAllConstraintsQueryData>(
    queryParams: operations['check_all_constraints_hospital_constraints_daily_check_all_constraints__post']['parameters']['query'],
    body: operations['check_all_constraints_hospital_constraints_daily_check_all_constraints__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckAllConstraintsQueryData, TData, CheckAllConstraintsQueryKey>
) => useQuery(getCheckAllConstraintsQueryOptions(queryParams, body, options))

/**
 * Check Available Times
 * 
 * Evaluate whether scheduling the candidate surgery for any of the given OR days would violate any
configured daily constraints.

Returns a OR-days with violations, including references to the violated constraint(s).
 */
type CheckAvailableTimesQueryData =
    operations['check_available_times_hospital_constraints_daily_check_available_times__post']['responses'][200]['content']['application/json']

const getCheckAvailableTimesQueryKey = (
    body: operations['check_available_times_hospital_constraints_daily_check_available_times__post']['requestBody']['content']['application/json']
) => ['view', 'checkAvailableTimes', body] as const
type CheckAvailableTimesQueryKey = ReturnType<typeof getCheckAvailableTimesQueryKey>

export const getCheckAvailableTimesQueryOptions = <TData = CheckAvailableTimesQueryData>(
    body: operations['check_available_times_hospital_constraints_daily_check_available_times__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckAvailableTimesQueryData, TData, CheckAvailableTimesQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getCheckAvailableTimesQueryKey(body),
        queryFn: ({ signal }) =>
            requireViewData(handleApiRequest(() => optimusBaseClient.POST('/hospital-constraints/daily/check_available_times/', { body, signal }))),
    })

export const useCheckAvailableTimes = <TData = CheckAvailableTimesQueryData>(
    body: operations['check_available_times_hospital_constraints_daily_check_available_times__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckAvailableTimesQueryData, TData, CheckAvailableTimesQueryKey>
) => useQuery(getCheckAvailableTimesQueryOptions(body, options))

/**
 * Check Available Times All
 * 
 * Combined evaluation of available times against both equipment daily constraints
and post-op capacity constraints.
 */
type CheckAvailableTimesAllQueryData =
    operations['check_available_times_all_hospital_constraints_daily_check_available_times_all__post']['responses'][200]['content']['application/json']

const getCheckAvailableTimesAllQueryKey = (
    body: operations['check_available_times_all_hospital_constraints_daily_check_available_times_all__post']['requestBody']['content']['application/json']
) => ['view', 'checkAvailableTimesAll', body] as const
type CheckAvailableTimesAllQueryKey = ReturnType<typeof getCheckAvailableTimesAllQueryKey>

export const getCheckAvailableTimesAllQueryOptions = <TData = CheckAvailableTimesAllQueryData>(
    body: operations['check_available_times_all_hospital_constraints_daily_check_available_times_all__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckAvailableTimesAllQueryData, TData, CheckAvailableTimesAllQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getCheckAvailableTimesAllQueryKey(body),
        queryFn: ({ signal }) =>
            requireViewData(handleApiRequest(() => optimusBaseClient.POST('/hospital-constraints/daily/check_available_times_all/', { body, signal }))),
    })

export const useCheckAvailableTimesAll = <TData = CheckAvailableTimesAllQueryData>(
    body: operations['check_available_times_all_hospital_constraints_daily_check_available_times_all__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckAvailableTimesAllQueryData, TData, CheckAvailableTimesAllQueryKey>
) => useQuery(getCheckAvailableTimesAllQueryOptions(body, options))

/**
 * Check Scheduled
 * 
 * Evaluate whether scheduled surgeries would violate any configured daily constraints.

Important: For each date in the request, make sure all surgeries for that date are included in the request.
Otherwise, the constraint evaluation will not be accurate.
 */
type CheckScheduledQueryData = operations['check_scheduled_hospital_constraints_daily_check_scheduled__post']['responses'][200]['content']['application/json']

const getCheckScheduledQueryKey = (
    body: operations['check_scheduled_hospital_constraints_daily_check_scheduled__post']['requestBody']['content']['application/json']
) => ['view', 'checkScheduled', body] as const
type CheckScheduledQueryKey = ReturnType<typeof getCheckScheduledQueryKey>

export const getCheckScheduledQueryOptions = <TData = CheckScheduledQueryData>(
    body: operations['check_scheduled_hospital_constraints_daily_check_scheduled__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckScheduledQueryData, TData, CheckScheduledQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getCheckScheduledQueryKey(body),
        queryFn: ({ signal }) =>
            requireViewData(handleApiRequest(() => optimusBaseClient.POST('/hospital-constraints/daily/check_scheduled/', { body, signal }))),
    })

export const useCheckScheduled = <TData = CheckScheduledQueryData>(
    body: operations['check_scheduled_hospital_constraints_daily_check_scheduled__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckScheduledQueryData, TData, CheckScheduledQueryKey>
) => useQuery(getCheckScheduledQueryOptions(body, options))

/**
 * Check Candidate Group
 * 
 * Evaluate whether scheduling the entire candidate group together for a given date at the given room would violate any
configured daily constraints (e.g. equipment availability).

The group is evaluated as a unit - all surgeries in the group are considered together when checking constraints.
Returns a list of candidate surgery ids and the constraints that would be violated by each surgery in the group.
 */
type CheckCandidateGroupQueryData =
    operations['check_candidate_group_hospital_constraints_daily__date__check_candidate_group__post']['responses'][200]['content']['application/json']

const getCheckCandidateGroupQueryKey = (
    pathParams: operations['check_candidate_group_hospital_constraints_daily__date__check_candidate_group__post']['parameters']['path'],
    body: operations['check_candidate_group_hospital_constraints_daily__date__check_candidate_group__post']['requestBody']['content']['application/json']
) => ['view', 'checkCandidateGroup', pathParams, body] as const
type CheckCandidateGroupQueryKey = ReturnType<typeof getCheckCandidateGroupQueryKey>

export const getCheckCandidateGroupQueryOptions = <TData = CheckCandidateGroupQueryData>(
    pathParams: operations['check_candidate_group_hospital_constraints_daily__date__check_candidate_group__post']['parameters']['path'],
    body: operations['check_candidate_group_hospital_constraints_daily__date__check_candidate_group__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckCandidateGroupQueryData, TData, CheckCandidateGroupQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getCheckCandidateGroupQueryKey(pathParams, body),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() =>
                    optimusBaseClient.POST('/hospital-constraints/daily/{date}/check_candidate_group/', { params: { path: pathParams }, body, signal })
                )
            ),
    })

export const useCheckCandidateGroup = <TData = CheckCandidateGroupQueryData>(
    pathParams: operations['check_candidate_group_hospital_constraints_daily__date__check_candidate_group__post']['parameters']['path'],
    body: operations['check_candidate_group_hospital_constraints_daily__date__check_candidate_group__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckCandidateGroupQueryData, TData, CheckCandidateGroupQueryKey>
) => useQuery(getCheckCandidateGroupQueryOptions(pathParams, body, options))

/**
 * Check Candidate Group All
 * 
 * Combined evaluation of a candidate group against both equipment daily constraints
and post-op capacity constraints.
 */
type CheckCandidateGroupAllQueryData =
    operations['check_candidate_group_all_hospital_constraints_daily__date__check_candidate_group_all__post']['responses'][200]['content']['application/json']

const getCheckCandidateGroupAllQueryKey = (
    pathParams: operations['check_candidate_group_all_hospital_constraints_daily__date__check_candidate_group_all__post']['parameters']['path'],
    body: operations['check_candidate_group_all_hospital_constraints_daily__date__check_candidate_group_all__post']['requestBody']['content']['application/json']
) => ['view', 'checkCandidateGroupAll', pathParams, body] as const
type CheckCandidateGroupAllQueryKey = ReturnType<typeof getCheckCandidateGroupAllQueryKey>

export const getCheckCandidateGroupAllQueryOptions = <TData = CheckCandidateGroupAllQueryData>(
    pathParams: operations['check_candidate_group_all_hospital_constraints_daily__date__check_candidate_group_all__post']['parameters']['path'],
    body: operations['check_candidate_group_all_hospital_constraints_daily__date__check_candidate_group_all__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckCandidateGroupAllQueryData, TData, CheckCandidateGroupAllQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getCheckCandidateGroupAllQueryKey(pathParams, body),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() =>
                    optimusBaseClient.POST('/hospital-constraints/daily/{date}/check_candidate_group_all/', { params: { path: pathParams }, body, signal })
                )
            ),
    })

export const useCheckCandidateGroupAll = <TData = CheckCandidateGroupAllQueryData>(
    pathParams: operations['check_candidate_group_all_hospital_constraints_daily__date__check_candidate_group_all__post']['parameters']['path'],
    body: operations['check_candidate_group_all_hospital_constraints_daily__date__check_candidate_group_all__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckCandidateGroupAllQueryData, TData, CheckCandidateGroupAllQueryKey>
) => useQuery(getCheckCandidateGroupAllQueryOptions(pathParams, body, options))

/**
 * Check Candidates
 * 
 * Evaluate whether scheduling any of the candidate surgeries for a given date at the given room would violate any
configured daily constraints (e.g. equipment availability).

Returns a list of candidate surgery ids and the constraints that would be violated by the given candidate.
 */
type CheckCandidatesQueryData =
    operations['check_candidates_hospital_constraints_daily__date__check_candidates__post']['responses'][200]['content']['application/json']

const getCheckCandidatesQueryKey = (
    pathParams: operations['check_candidates_hospital_constraints_daily__date__check_candidates__post']['parameters']['path'],
    body: operations['check_candidates_hospital_constraints_daily__date__check_candidates__post']['requestBody']['content']['application/json']
) => ['view', 'checkCandidates', pathParams, body] as const
type CheckCandidatesQueryKey = ReturnType<typeof getCheckCandidatesQueryKey>

export const getCheckCandidatesQueryOptions = <TData = CheckCandidatesQueryData>(
    pathParams: operations['check_candidates_hospital_constraints_daily__date__check_candidates__post']['parameters']['path'],
    body: operations['check_candidates_hospital_constraints_daily__date__check_candidates__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckCandidatesQueryData, TData, CheckCandidatesQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getCheckCandidatesQueryKey(pathParams, body),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() =>
                    optimusBaseClient.POST('/hospital-constraints/daily/{date}/check_candidates/', { params: { path: pathParams }, body, signal })
                )
            ),
    })

export const useCheckCandidates = <TData = CheckCandidatesQueryData>(
    pathParams: operations['check_candidates_hospital_constraints_daily__date__check_candidates__post']['parameters']['path'],
    body: operations['check_candidates_hospital_constraints_daily__date__check_candidates__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckCandidatesQueryData, TData, CheckCandidatesQueryKey>
) => useQuery(getCheckCandidatesQueryOptions(pathParams, body, options))

/**
 * Check Candidates All
 * 
 * Combined evaluation of candidate surgeries against both equipment daily constraints
and post-op capacity constraints.
 */
type CheckCandidatesAllQueryData =
    operations['check_candidates_all_hospital_constraints_daily__date__check_candidates_all__post']['responses'][200]['content']['application/json']

const getCheckCandidatesAllQueryKey = (
    pathParams: operations['check_candidates_all_hospital_constraints_daily__date__check_candidates_all__post']['parameters']['path'],
    body: operations['check_candidates_all_hospital_constraints_daily__date__check_candidates_all__post']['requestBody']['content']['application/json']
) => ['view', 'checkCandidatesAll', pathParams, body] as const
type CheckCandidatesAllQueryKey = ReturnType<typeof getCheckCandidatesAllQueryKey>

export const getCheckCandidatesAllQueryOptions = <TData = CheckCandidatesAllQueryData>(
    pathParams: operations['check_candidates_all_hospital_constraints_daily__date__check_candidates_all__post']['parameters']['path'],
    body: operations['check_candidates_all_hospital_constraints_daily__date__check_candidates_all__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckCandidatesAllQueryData, TData, CheckCandidatesAllQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getCheckCandidatesAllQueryKey(pathParams, body),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() =>
                    optimusBaseClient.POST('/hospital-constraints/daily/{date}/check_candidates_all/', { params: { path: pathParams }, body, signal })
                )
            ),
    })

export const useCheckCandidatesAll = <TData = CheckCandidatesAllQueryData>(
    pathParams: operations['check_candidates_all_hospital_constraints_daily__date__check_candidates_all__post']['parameters']['path'],
    body: operations['check_candidates_all_hospital_constraints_daily__date__check_candidates_all__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<CheckCandidatesAllQueryData, TData, CheckCandidatesAllQueryKey>
) => useQuery(getCheckCandidatesAllQueryOptions(pathParams, body, options))

/**
 * Postop Status Booking
 */
type PostopStatusBookingQueryData =
    operations['postop_status_booking_hospital_constraints_postop_booking_status__post']['responses'][200]['content']['application/json']

const getPostopStatusBookingQueryKey = (
    queryParams: operations['postop_status_booking_hospital_constraints_postop_booking_status__post']['parameters']['query'],
    body: operations['postop_status_booking_hospital_constraints_postop_booking_status__post']['requestBody']['content']['application/json']
) => ['view', 'postopStatusBooking', queryParams, body] as const
type PostopStatusBookingQueryKey = ReturnType<typeof getPostopStatusBookingQueryKey>

export const getPostopStatusBookingQueryOptions = <TData = PostopStatusBookingQueryData>(
    queryParams: operations['postop_status_booking_hospital_constraints_postop_booking_status__post']['parameters']['query'],
    body: operations['postop_status_booking_hospital_constraints_postop_booking_status__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<PostopStatusBookingQueryData, TData, PostopStatusBookingQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getPostopStatusBookingQueryKey(queryParams, body),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() => optimusBaseClient.POST('/hospital-constraints/postop/booking/status/', { params: { query: queryParams }, body, signal }))
            ),
    })

export const usePostopStatusBooking = <TData = PostopStatusBookingQueryData>(
    queryParams: operations['postop_status_booking_hospital_constraints_postop_booking_status__post']['parameters']['query'],
    body: operations['postop_status_booking_hospital_constraints_postop_booking_status__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<PostopStatusBookingQueryData, TData, PostopStatusBookingQueryKey>
) => useQuery(getPostopStatusBookingQueryOptions(queryParams, body, options))

/**
 * Postop Check Candidate Group
 * 
 * Evaluate whether scheduling the entire candidate group together for a given date at the given room would violate any
configured post-op constraints.

Returns a list of post-op constraint violations that would occur if the candidate group were scheduled.
Each violation item contains the candidate surgery id and the violated post-op's status.
 */
type PostopCheckCandidateGroupQueryData =
    operations['postop_check_candidate_group_hospital_constraints_postop_booking__date__check_candidate_group__post']['responses'][200]['content']['application/json']

const getPostopCheckCandidateGroupQueryKey = (
    pathParams: operations['postop_check_candidate_group_hospital_constraints_postop_booking__date__check_candidate_group__post']['parameters']['path'],
    body: operations['postop_check_candidate_group_hospital_constraints_postop_booking__date__check_candidate_group__post']['requestBody']['content']['application/json']
) => ['view', 'postopCheckCandidateGroup', pathParams, body] as const
type PostopCheckCandidateGroupQueryKey = ReturnType<typeof getPostopCheckCandidateGroupQueryKey>

export const getPostopCheckCandidateGroupQueryOptions = <TData = PostopCheckCandidateGroupQueryData>(
    pathParams: operations['postop_check_candidate_group_hospital_constraints_postop_booking__date__check_candidate_group__post']['parameters']['path'],
    body: operations['postop_check_candidate_group_hospital_constraints_postop_booking__date__check_candidate_group__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<PostopCheckCandidateGroupQueryData, TData, PostopCheckCandidateGroupQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getPostopCheckCandidateGroupQueryKey(pathParams, body),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() =>
                    optimusBaseClient.POST('/hospital-constraints/postop/booking/{date}/check_candidate_group/', { params: { path: pathParams }, body, signal })
                )
            ),
    })

export const usePostopCheckCandidateGroup = <TData = PostopCheckCandidateGroupQueryData>(
    pathParams: operations['postop_check_candidate_group_hospital_constraints_postop_booking__date__check_candidate_group__post']['parameters']['path'],
    body: operations['postop_check_candidate_group_hospital_constraints_postop_booking__date__check_candidate_group__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<PostopCheckCandidateGroupQueryData, TData, PostopCheckCandidateGroupQueryKey>
) => useQuery(getPostopCheckCandidateGroupQueryOptions(pathParams, body, options))

/**
 * Postop Check Candidates
 * 
 * Evaluate whether scheduling any of the candidate surgeries individually for a given date at the given room
would violate any configured post-op constraints.

Each candidate is evaluated independently - if scheduling just that one candidate would cause a violation,
it is included in the response.
 */
type PostopCheckCandidatesQueryData =
    operations['postop_check_candidates_hospital_constraints_postop_booking__date__check_candidates__post']['responses'][200]['content']['application/json']

const getPostopCheckCandidatesQueryKey = (
    pathParams: operations['postop_check_candidates_hospital_constraints_postop_booking__date__check_candidates__post']['parameters']['path'],
    body: operations['postop_check_candidates_hospital_constraints_postop_booking__date__check_candidates__post']['requestBody']['content']['application/json']
) => ['view', 'postopCheckCandidates', pathParams, body] as const
type PostopCheckCandidatesQueryKey = ReturnType<typeof getPostopCheckCandidatesQueryKey>

export const getPostopCheckCandidatesQueryOptions = <TData = PostopCheckCandidatesQueryData>(
    pathParams: operations['postop_check_candidates_hospital_constraints_postop_booking__date__check_candidates__post']['parameters']['path'],
    body: operations['postop_check_candidates_hospital_constraints_postop_booking__date__check_candidates__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<PostopCheckCandidatesQueryData, TData, PostopCheckCandidatesQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getPostopCheckCandidatesQueryKey(pathParams, body),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() =>
                    optimusBaseClient.POST('/hospital-constraints/postop/booking/{date}/check_candidates/', { params: { path: pathParams }, body, signal })
                )
            ),
    })

export const usePostopCheckCandidates = <TData = PostopCheckCandidatesQueryData>(
    pathParams: operations['postop_check_candidates_hospital_constraints_postop_booking__date__check_candidates__post']['parameters']['path'],
    body: operations['postop_check_candidates_hospital_constraints_postop_booking__date__check_candidates__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<PostopCheckCandidatesQueryData, TData, PostopCheckCandidatesQueryKey>
) => useQuery(getPostopCheckCandidatesQueryOptions(pathParams, body, options))

/**
 * Postop Check Available Times
 * 
 * Evaluate whether scheduling the candidate surgery for any of the given OR days would violate any
configured post-op constraints.

Returns OR-days with violations, including references to the violated post-op unit(s).
 */
type PostopCheckAvailableTimesQueryData =
    operations['postop_check_available_times_hospital_constraints_postop_check_available_times__post']['responses'][200]['content']['application/json']

const getPostopCheckAvailableTimesQueryKey = (
    body: operations['postop_check_available_times_hospital_constraints_postop_check_available_times__post']['requestBody']['content']['application/json']
) => ['view', 'postopCheckAvailableTimes', body] as const
type PostopCheckAvailableTimesQueryKey = ReturnType<typeof getPostopCheckAvailableTimesQueryKey>

export const getPostopCheckAvailableTimesQueryOptions = <TData = PostopCheckAvailableTimesQueryData>(
    body: operations['postop_check_available_times_hospital_constraints_postop_check_available_times__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<PostopCheckAvailableTimesQueryData, TData, PostopCheckAvailableTimesQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getPostopCheckAvailableTimesQueryKey(body),
        queryFn: ({ signal }) =>
            requireViewData(handleApiRequest(() => optimusBaseClient.POST('/hospital-constraints/postop/check_available_times/', { body, signal }))),
    })

export const usePostopCheckAvailableTimes = <TData = PostopCheckAvailableTimesQueryData>(
    body: operations['postop_check_available_times_hospital_constraints_postop_check_available_times__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<PostopCheckAvailableTimesQueryData, TData, PostopCheckAvailableTimesQueryKey>
) => useQuery(getPostopCheckAvailableTimesQueryOptions(body, options))

/**
 * Postop Status Tactical Plan
 */
type PostopStatusTacticalPlanQueryData =
    operations['postop_status_tactical_plan_hospital_constraints_postop_tactical_plan_status__get']['responses'][200]['content']['application/json']

const getPostopStatusTacticalPlanQueryKey = (
    queryParams: operations['postop_status_tactical_plan_hospital_constraints_postop_tactical_plan_status__get']['parameters']['query']
) => ['view', 'postopStatusTacticalPlan', queryParams] as const
type PostopStatusTacticalPlanQueryKey = ReturnType<typeof getPostopStatusTacticalPlanQueryKey>

export const getPostopStatusTacticalPlanQueryOptions = <TData = PostopStatusTacticalPlanQueryData>(
    queryParams: operations['postop_status_tactical_plan_hospital_constraints_postop_tactical_plan_status__get']['parameters']['query'],
    options?: ViewQueryOptions<PostopStatusTacticalPlanQueryData, TData, PostopStatusTacticalPlanQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getPostopStatusTacticalPlanQueryKey(queryParams),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() => optimusBaseClient.GET('/hospital-constraints/postop/tactical-plan/status/', { params: { query: queryParams }, signal }))
            ),
    })

export const usePostopStatusTacticalPlan = <TData = PostopStatusTacticalPlanQueryData>(
    queryParams: operations['postop_status_tactical_plan_hospital_constraints_postop_tactical_plan_status__get']['parameters']['query'],
    options?: ViewQueryOptions<PostopStatusTacticalPlanQueryData, TData, PostopStatusTacticalPlanQueryKey>
) => useQuery(getPostopStatusTacticalPlanQueryOptions(queryParams, options))

/**
 * Get Plan Changes
 */
type PlanChangesQueryData = operations['get_plan_changes_plan_metrics_plan_changes__get']['responses'][200]['content']['application/json']

const getPlanChangesQueryKey = (queryParams: operations['get_plan_changes_plan_metrics_plan_changes__get']['parameters']['query']) =>
    ['view', 'PlanChanges', queryParams] as const
type PlanChangesQueryKey = ReturnType<typeof getPlanChangesQueryKey>

export const getPlanChangesQueryOptions = <TData = PlanChangesQueryData>(
    queryParams: operations['get_plan_changes_plan_metrics_plan_changes__get']['parameters']['query'],
    options?: ViewQueryOptions<PlanChangesQueryData, TData, PlanChangesQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getPlanChangesQueryKey(queryParams),
        queryFn: ({ signal }) =>
            requireViewData(handleApiRequest(() => optimusBaseClient.GET('/plan-metrics/plan_changes/', { params: { query: queryParams }, signal }))),
    })

export const usePlanChanges = <TData = PlanChangesQueryData>(
    queryParams: operations['get_plan_changes_plan_metrics_plan_changes__get']['parameters']['query'],
    options?: ViewQueryOptions<PlanChangesQueryData, TData, PlanChangesQueryKey>
) => useQuery(getPlanChangesQueryOptions(queryParams, options))

/**
 * Get Planning Period Planned Surgeries Timeline
 */
type PlanningPeriodPlannedSurgeriesTimelineQueryData =
    operations['get_planning_period_planned_surgeries_timeline_plan_metrics_planning_periods_planned_surgeries_timeline__planning_period_id__get']['responses'][200]['content']['application/json']

const getPlanningPeriodPlannedSurgeriesTimelineQueryKey = (
    pathParams: operations['get_planning_period_planned_surgeries_timeline_plan_metrics_planning_periods_planned_surgeries_timeline__planning_period_id__get']['parameters']['path']
) => ['view', 'PlanningPeriodPlannedSurgeriesTimeline', pathParams] as const
type PlanningPeriodPlannedSurgeriesTimelineQueryKey = ReturnType<typeof getPlanningPeriodPlannedSurgeriesTimelineQueryKey>

export const getPlanningPeriodPlannedSurgeriesTimelineQueryOptions = <TData = PlanningPeriodPlannedSurgeriesTimelineQueryData>(
    pathParams: operations['get_planning_period_planned_surgeries_timeline_plan_metrics_planning_periods_planned_surgeries_timeline__planning_period_id__get']['parameters']['path'],
    options?: ViewQueryOptions<PlanningPeriodPlannedSurgeriesTimelineQueryData, TData, PlanningPeriodPlannedSurgeriesTimelineQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getPlanningPeriodPlannedSurgeriesTimelineQueryKey(pathParams),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() =>
                    optimusBaseClient.GET('/plan-metrics/planning_periods/planned_surgeries_timeline/{planning_period_id}', {
                        params: { path: pathParams },
                        signal,
                    })
                )
            ),
    })

export const usePlanningPeriodPlannedSurgeriesTimeline = <TData = PlanningPeriodPlannedSurgeriesTimelineQueryData>(
    pathParams: operations['get_planning_period_planned_surgeries_timeline_plan_metrics_planning_periods_planned_surgeries_timeline__planning_period_id__get']['parameters']['path'],
    options?: ViewQueryOptions<PlanningPeriodPlannedSurgeriesTimelineQueryData, TData, PlanningPeriodPlannedSurgeriesTimelineQueryKey>
) => useQuery(getPlanningPeriodPlannedSurgeriesTimelineQueryOptions(pathParams, options))

/**
 * Get Planning Period Metrics
 */
type PlanningPeriodMetricsQueryData =
    operations['get_planning_period_metrics_plan_metrics_planning_periods__planning_period_id__get']['responses'][200]['content']['application/json']

const getPlanningPeriodMetricsQueryKey = (
    pathParams: operations['get_planning_period_metrics_plan_metrics_planning_periods__planning_period_id__get']['parameters']['path']
) => ['view', 'PlanningPeriodMetrics', pathParams] as const
type PlanningPeriodMetricsQueryKey = ReturnType<typeof getPlanningPeriodMetricsQueryKey>

export const getPlanningPeriodMetricsQueryOptions = <TData = PlanningPeriodMetricsQueryData>(
    pathParams: operations['get_planning_period_metrics_plan_metrics_planning_periods__planning_period_id__get']['parameters']['path'],
    options?: ViewQueryOptions<PlanningPeriodMetricsQueryData, TData, PlanningPeriodMetricsQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getPlanningPeriodMetricsQueryKey(pathParams),
        queryFn: ({ signal }) =>
            requireViewData(
                handleApiRequest(() => optimusBaseClient.GET('/plan-metrics/planning_periods/{planning_period_id}', { params: { path: pathParams }, signal }))
            ),
    })

export const usePlanningPeriodMetrics = <TData = PlanningPeriodMetricsQueryData>(
    pathParams: operations['get_planning_period_metrics_plan_metrics_planning_periods__planning_period_id__get']['parameters']['path'],
    options?: ViewQueryOptions<PlanningPeriodMetricsQueryData, TData, PlanningPeriodMetricsQueryKey>
) => useQuery(getPlanningPeriodMetricsQueryOptions(pathParams, options))

/**
 * Get Surgeons Waiting List Diff
 *
 * Calculate the incoming surgeries based on the surgeon's outpatient clinic activity (P, KvP)
 */
type SurgeonsWaitingListDiffQueryData = operations['get_surgeons_waiting_list_diff_waiting_list__post']['responses'][200]['content']['application/json']

const getSurgeonsWaitingListDiffQueryKey = (
    body: operations['get_surgeons_waiting_list_diff_waiting_list__post']['requestBody']['content']['application/json']
) => ['view', 'SurgeonsWaitingListDiff', body] as const
type SurgeonsWaitingListDiffQueryKey = ReturnType<typeof getSurgeonsWaitingListDiffQueryKey>

export const getSurgeonsWaitingListDiffQueryOptions = <TData = SurgeonsWaitingListDiffQueryData>(
    body: operations['get_surgeons_waiting_list_diff_waiting_list__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<SurgeonsWaitingListDiffQueryData, TData, SurgeonsWaitingListDiffQueryKey>
) =>
    queryOptions({
        placeholderData: keepPreviousData,
        staleTime: 300000,
        ...options,
        queryKey: getSurgeonsWaitingListDiffQueryKey(body),
        queryFn: ({ signal }) => requireViewData(handleApiRequest(() => optimusBaseClient.POST('/waiting-list/', { body, signal }))),
    })

export const useSurgeonsWaitingListDiff = <TData = SurgeonsWaitingListDiffQueryData>(
    body: operations['get_surgeons_waiting_list_diff_waiting_list__post']['requestBody']['content']['application/json'],
    options?: ViewQueryOptions<SurgeonsWaitingListDiffQueryData, TData, SurgeonsWaitingListDiffQueryKey>
) => useQuery(getSurgeonsWaitingListDiffQueryOptions(body, options))

export const viewQueryInvalidationMap: Record<string, string[]> = {
    DailyConstraints: ['hospital_surgery_type', 'location', 'surgery_type_group'],
    checkAllConstraints: [
        'block_schedule',
        'hospital_surgery_type_group_association',
        'location',
        'location_post_op_association',
        'post_op',
        'post_op_capacity_override',
        'post_op_capacity_period',
        'rule_instance',
        'surgery_type_group',
        'surgery_type_group_hierarchy',
    ],
    checkAvailableTimes: ['block_schedule', 'hospital_surgery_type_group_association', 'location', 'surgery_type_group', 'surgery_type_group_hierarchy'],
    checkAvailableTimesAll: [
        'block_schedule',
        'hospital_surgery_type_group_association',
        'location',
        'location_post_op_association',
        'post_op',
        'post_op_capacity_override',
        'post_op_capacity_period',
        'rule_instance',
        'surgery_type_group',
        'surgery_type_group_hierarchy',
    ],
    checkScheduled: ['hospital_surgery_type_group_association', 'location', 'surgery_type_group', 'surgery_type_group_hierarchy'],
    checkCandidateGroup: ['hospital_surgery_type_group_association', 'location', 'surgery_type_group', 'surgery_type_group_hierarchy'],
    checkCandidateGroupAll: [
        'hospital_surgery_type_group_association',
        'location',
        'location_post_op_association',
        'post_op',
        'post_op_capacity_override',
        'post_op_capacity_period',
        'surgery_type_group',
        'surgery_type_group_hierarchy',
    ],
    checkCandidates: ['hospital_surgery_type_group_association', 'location', 'surgery_type_group', 'surgery_type_group_hierarchy'],
    checkCandidatesAll: [
        'hospital_surgery_type_group_association',
        'location',
        'location_post_op_association',
        'post_op',
        'post_op_capacity_override',
        'post_op_capacity_period',
        'surgery_type_group',
        'surgery_type_group_hierarchy',
    ],
    postopStatusBooking: [
        'block_schedule',
        'location',
        'location_post_op_association',
        'post_op',
        'post_op_capacity_override',
        'post_op_capacity_period',
        'rule_instance',
    ],
    postopCheckCandidateGroup: [
        'block_schedule',
        'location',
        'location_post_op_association',
        'post_op',
        'post_op_capacity_override',
        'post_op_capacity_period',
        'rule_instance',
    ],
    postopCheckCandidates: [
        'block_schedule',
        'location',
        'location_post_op_association',
        'post_op',
        'post_op_capacity_override',
        'post_op_capacity_period',
        'rule_instance',
    ],
    postopCheckAvailableTimes: [
        'block_schedule',
        'location',
        'location_post_op_association',
        'post_op',
        'post_op_capacity_override',
        'post_op_capacity_period',
        'rule_instance',
    ],
    postopStatusTacticalPlan: [
        'block_schedule',
        'location',
        'location_post_op_association',
        'post_op',
        'post_op_capacity_override',
        'post_op_capacity_period',
        'rule_instance',
    ],
    PlanChanges: ['block_schedule', 'location', 'planning_period', 'rule_instance', 'tactical_plan_snapshot'],
    PlanningPeriodPlannedSurgeriesTimeline: ['block_schedule', 'planning_period', 'rule_instance'],
    PlanningPeriodMetrics: ['block_schedule', 'location', 'location_service_override', 'planning_period', 'practitioner_schedule_location', 'rule_instance'],
    SurgeonsWaitingListDiff: [
        'age_group',
        'block_schedule',
        'hospital_surgery_type',
        'hospital_surgery_type_group_association',
        'location',
        'practitioner',
        'practitioner_schedule_location',
        'practitioner_schedule_status',
        'rule_instance',
        'speciality',
        'surgery_type_group',
        'surgery_type_group_age_restriction',
        'surgery_type_group_hierarchy',
    ],
}
